/* eslint-disable react-hooks/exhaustive-deps */
import { Flex, Box, Text, useToast } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import AlertComponent from "components/Alert";
import {
  useCreateProcessCommentMutation,
  useGetProcessCommentsQuery,
} from "redux/services/approvalProcessor";
import LoadingOverlay from "components/LoadingOverlay";
import { DatasetDownloadResponse } from "redux/services/dataLoader/types";
import {
  getOrganisationMembership,
  renderContentTypeAsExtensionFile,
} from "utils";
import { useGetDocumentMutation } from "redux/services/documentRepository";
import {
  ApprovalTaskStatues,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  Roles,
} from "common";
import { useGetProfileQuery } from "redux/services/membership";
import {
  ProcessComment,
  ProcessCommentsRequest,
} from "redux/services/approvalProcessor/types";
import DisburseConfirmationDialog from "pages/ApprovalDetail/DisburseConfirmationDialog";
import { useRoles } from "hooks/useRoles";
import { useApprovalDetailsService } from "pages/ApprovalDetail/ApprovalDetails.service";
import ApprovalDetailsContent from "pages/ApprovalDetail/ApprovalDetailsContent";

const ApprovalDetails = () => {
  const [selectedDisbursementDate, setSelectedDisbursementDate] = useState<
    Date | undefined
  >();

  const toast = useToast();

  const {
    approvalTasks,
    isGetApprovalTasksLoading,
    isGetApprovalTasksError,
    isEnableForChangeStatus,
    isMissingPrefund,
    isMissingDisbursementDate,
    isMissingDisbursementReport,
    missingPrefundAndDisbursementDateMessage,
    getApprovalTasks,
    getApprovalStatus,
  } = useApprovalDetailsService(selectedDisbursementDate);

  const { data: ProfileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    ProfileData?.data?.memberships
  );
  const orgToken = membershipOrganisation?.token;

  const [selectedProofDocument, setSelectedProofDocument] = useState(undefined);
  const [selectedPayrollFile, setSelectedPayrollFile] = useState(undefined);
  const [firstButtonText, setFirstButtonText] = useState("");
  const [secondButtonText, setSecondButtonText] = useState("");

  const { isPaymentOpsRole, isInhouseApproverRole, isPSPApproverRole, isInHouseCheckerRole, isPSPCheckerRole } = useRoles();

  const [disburseDialogState, setDisburseDialogState] = useState<{
    isOpen: boolean;
  }>({ isOpen: false });

  const openDisburseDialog = () => {
    setDisburseDialogState({ isOpen: true });
  };

  const isBusinessApproverRole = isInHouseCheckerRole || isPSPCheckerRole;

  const [getProcessCommentsParamsRequest, setGetProcessCommentsParamsRequest] =
    useState<ProcessCommentsRequest>({
      orgToken,
      pageNumber: DEFAULT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE / 2,
      processId: "",
    });

  const [commentValue, setCommentValue] = useState<string>();
  const [commentsData, setCommentsData] = useState<ProcessComment[]>([]);

  const {
    data: processComments,
    isFetching: isFetchingProcessComments,
    isLoading: isLoadingProcessComments,
  } = useGetProcessCommentsQuery(getProcessCommentsParamsRequest, {
    skip: !getProcessCommentsParamsRequest.processId,
    refetchOnMountOrArgChange: true,
  });

  const isLoadMoreComments =
    getProcessCommentsParamsRequest.pageNumber <
    Math.ceil(
      (processComments?.paging?.totalRecords || 0) /
      getProcessCommentsParamsRequest.pageSize
    );

  const [
    createComment,
    { isLoading: createCommentLoading, data: createCommentData },
  ] = useCreateProcessCommentMutation();

  const approvalTask = approvalTasks?.data[0];

  const approverProcessed = approvalTask?.approvalTasks?.find(
    (appover) =>
      appover?.status === ApprovalTaskStatues.APPROVED &&
      appover.levelId === Roles.PaymentOps
  );

  const approverApproved = approvalTask?.approvalTasks?.find(
    (appover) =>
      appover?.status === ApprovalTaskStatues.APPROVED &&
      appover.levelId === Roles.BUChecker
  );

  const approverRejected = approvalTask?.approvalTasks?.find(
    (appover) => appover?.status === ApprovalTaskStatues.REJECTED
  );

  const buApprover = approvalTask?.approvalTasks?.find(
    (appover) =>
      appover?.status === ApprovalTaskStatues.APPROVED &&
      appover.levelId === Roles.BUApprover
  );

  const [
    downloadFile,
    { isSuccess: isDownloadDataFetchSuccess, data: downloadedFile },
  ] = useGetDocumentMutation();

  const [
    downloadProofFile,
    { isSuccess: isDownloadProofDocumentSuccess, data: proofFile },
  ] = useGetDocumentMutation();

  useEffect(() => {
    if (downloadedFile) {
      saveDownload(downloadedFile);
    }
  }, [downloadedFile, isDownloadDataFetchSuccess]);

  useEffect(() => {
    if (proofFile) {
      if (renderContentTypeAsExtensionFile(proofFile?.data?.name)) {
        const bs64file = proofFile?.data?.content;
        const linkSource = `data:${renderContentTypeAsExtensionFile(
          proofFile?.data?.name
        )};base64,${bs64file}`;
        const downloadLink = document.createElement("a");
        downloadLink.href = linkSource;
        downloadLink.download = proofFile?.data?.name as string;
        downloadLink.click();
        downloadLink.remove();
      } else {
        toast({
          title: "The file extension is not supported",
          description: "",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
      }

      setSelectedProofDocument(undefined);
    }
  }, [isDownloadProofDocumentSuccess, proofFile]);

  const saveDownload = (datatoDownload: DatasetDownloadResponse) => {
    if (approvalTasks?.data && approvalTasks?.data?.length > 0) {
      const approvalTask = approvalTasks?.data[0];
      const overallInformation = JSON.parse(
        approvalTask?.processVariables?.overallInformation
      );
      if (datatoDownload?.data?.content) {
        if (renderContentTypeAsExtensionFile(datatoDownload?.data?.name)) {
          const bs64file = datatoDownload?.data?.content;
          const linkSource = `data:application/csv;base64,${bs64file}`;
          const downloadLink = document.createElement("a");
          downloadLink.href = linkSource;
          downloadLink.download = overallInformation?.payrollDocumentName;
          downloadLink.click();
          downloadLink.remove();
        } else {
          toast({
            title: "The file extension is not supported",
            description: "",
            status: "error",
            duration: 9000,
            position: "top-right",
            isClosable: true,
          });
        }
      }
      setSelectedPayrollFile(undefined);
    }
  };

  useEffect(() => {
    if (selectedPayrollFile) {
      downloadFile({ documentId: selectedPayrollFile, orgToken });
    }
  }, [selectedPayrollFile]);

  useEffect(() => {
    if (selectedProofDocument) {
      downloadProofFile({ documentId: selectedProofDocument, orgToken });
    }
  }, [selectedProofDocument]);

  useEffect(() => {
    if (approvalTask)
      setGetProcessCommentsParamsRequest({
        ...getProcessCommentsParamsRequest,
        pageNumber: DEFAULT_PAGE,
        processId: approvalTask?.id,
      });
  }, [approvalTask]);

  useEffect(() => {
    if (createCommentData?.data) {
      setCommentValue("");
      setCommentsData([createCommentData?.data, ...commentsData]);
    }
  }, [createCommentData]);

  useEffect(() => {
    const dataFetching = processComments?.data || [];
    const isFirstFetching =
      getProcessCommentsParamsRequest.pageNumber === DEFAULT_PAGE;
    setCommentsData(
      isFirstFetching ? dataFetching : [...commentsData, ...dataFetching]
    );
  }, [processComments]);

  useEffect(() => {
    if ((isInHouseCheckerRole || isPSPCheckerRole) && getApprovalStatus()?.label === "Submitted") {
      setFirstButtonText("Proceed");
      setSecondButtonText("Reject");
      return;
    }

    if (
      isPaymentOpsRole &&
      (getApprovalStatus()?.label === "Reviewed" ||
        getApprovalStatus()?.label === "Approved")
    ) {
      setFirstButtonText("Process");
      setSecondButtonText("Decline");
      return;
    }

    if ((isInhouseApproverRole || isPSPApproverRole) && getApprovalStatus()?.label === "Reviewed") {
      setFirstButtonText("Approve");
      setSecondButtonText("Disapprove");
    }
  }, [isBusinessApproverRole, getApprovalStatus, isPaymentOpsRole]);

  const renderContent = () => {
    if (approvalTasks?.data && approvalTasks?.data?.length > 0) {
      return (
        <ApprovalDetailsContent
          getApprovalStatus={getApprovalStatus}
          approverProcessed={approverProcessed}
          approverRejected={approverRejected}
          approverApproved={approverApproved}
          buApprover={buApprover}
          isEnableForChangeStatus={isEnableForChangeStatus}
          firstButtonText={firstButtonText}
          secondButtonText={secondButtonText}
          openDisburseDialog={openDisburseDialog}
          isLoadingProcessComments={isLoadingProcessComments}
          commentsData={commentsData}
          isFetchingProcessComments={isFetchingProcessComments}
          isLoadMoreComments={isLoadMoreComments}
          getProcessCommentsParamsRequest={getProcessCommentsParamsRequest}
          setGetProcessCommentsParamsRequest={
            setGetProcessCommentsParamsRequest
          }
          createCommentLoading={createCommentLoading}
          commentValue={commentValue}
          createComment={createComment}
          orgToken={orgToken}
          approvalTask={approvalTask}
          setSelectedProofDocument={setSelectedProofDocument}
          setSelectedPayrollFile={setSelectedPayrollFile}
          ProfileData={ProfileData}
          setCommentValue={setCommentValue}
          getApprovalTasks={getApprovalTasks}
          setSelectedDisbursementDate={setSelectedDisbursementDate}
          selectedDisbursementDate={selectedDisbursementDate}
        />
      );
    }
  };

  return (
    <Flex
      w="100%"
      h="100%"
      p="20px"
      flexDir={"column"}
      className="ApprovalDetail"
    >
      <Text textStyle={"h1"}>
        {isPaymentOpsRole && "Payment Acknowledgement"}
        {(isBusinessApproverRole || isInhouseApproverRole || isPSPApproverRole) && "Payment Approval "}
        {!isPaymentOpsRole && (isInhouseApproverRole || isPSPApproverRole) && "by Approver"}
        {!isPaymentOpsRole && (isInHouseCheckerRole || isPSPCheckerRole) && "by Checker"}
      </Text>
      {(isMissingPrefund || isMissingDisbursementDate) && (
        <Text textStyle="h3" textColor="red.500" mt="10px" fontSize="16px">
          {missingPrefundAndDisbursementDateMessage}
        </Text>
      )}

      {isMissingDisbursementReport && getApprovalStatus()?.label === "Approved" && (
        <Text textStyle="h3" textColor="red.500" mt="10px" fontSize="16px">
          Please Upload the Disbursement Report to Process
        </Text>
      )}

      <Box
        w={"100%"}
        mt={"20px"}
      >
        {!isGetApprovalTasksLoading &&
          (!approvalTasks?.data ||
            approvalTasks?.data?.length === 0 ||
            isGetApprovalTasksError) && (
            <AlertComponent
              status="warning"
              title="Cannot found this payroll request"
            />
          )}

        {isGetApprovalTasksLoading ? (
          <LoadingOverlay data-testid="loading-screen" h="100%" />
        ) : (
          renderContent()
        )}
      </Box>

      <DisburseConfirmationDialog
        {...disburseDialogState}
        approvalTask={approvalTask}
        onClose={() => setDisburseDialogState({ isOpen: false })}
        onSuccess={() => {
          getApprovalTasks();
        }}
      />
    </Flex>
  );
};

export default ApprovalDetails;
